import { camelCase } from 'change-case'

import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import type { Api } from '@/services/api/api'
import type { AIGenerateTemplateEntry } from '@/services/api/api.types'

import type { FormType } from '../types/aiFieldGeneration'

// Helper to get ID parameters based on type
export function getIdParams(type: FormType, props: CMFormSection) {
  switch (type) {
    case 'cm':
      return { task_id: props.taskId! }
    case 'step':
      return { step_id: props.stepId! }
  }
}

// Helper to get the appropriate form based on type
export function getForm(props: CMFormSection, type: FormType) {
  switch (type) {
    case 'cm':
      return props.moduleForm
    case 'step':
      return props.stepForm
    default:
      return undefined
  }
}

// Centralized error logging
export function logAIGenerationError(context: string, error: unknown, fieldName?: string) {
  console.error(`[AI Generation Error] ${context}:`, {
    fieldName,
    error: error instanceof Error ? error.message : String(error),
    timestamp: new Date().toISOString(),
  })
}

/**
 * The key an `ai_generate_templates` entry arrives under. The backend stores each result
 * against its raw template id, and the response transform rewrites map keys as well as
 * field names, so `generate_cm_outline` reaches us as `generateCmOutline`. Anything
 * looking an entry up by the id it sent has to convert first.
 */
export const aiTemplateKey = (systemPromptTemplateId: string): string => camelCase(systemPromptTemplateId)

export function normalizeAISuggestion(raw: string): string {
  let result = raw.trim()
  const fenceMatch = result.match(/^```(?:\w+)?\s*\n([\s\S]*?)\n\s*```\s*$/)
  if (fenceMatch) {
    result = fenceMatch[1].trim()
  }
  return result.replace(/\n\s*\n/g, '\n\n')
}

export type PollEntityType = 'cm' | 'step' | 'content'

export type PollResult =
  | { status: 'done'; result: string }
  | { status: 'failed'; error: string }
  | { status: 'timeout' }
  | { status: 'aborted' }

const POLL_DEFAULTS = { maxAttempts: 90, intervalMs: 4000 }
const MAX_CONSECUTIVE_FAILURES = 5

/**
 * Polls an entity's `aiGenerateTemplates` field until the generation for a
 * given template ID reports "done" or "failed", the caller aborts, or we
 * exceed the maximum number of attempts.
 */
export async function pollForGenerationResult(
  api: Api,
  params: { entityType: PollEntityType; entityId: string; systemPromptTemplateId: string },
  options?: { maxAttempts?: number; intervalMs?: number; signal?: AbortSignal },
): Promise<PollResult> {
  const { maxAttempts, intervalMs, signal } = { ...POLL_DEFAULTS, ...options }
  const { entityType, entityId, systemPromptTemplateId } = params
  let consecutiveFailures = 0

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) return { status: 'aborted' }

    await new Promise(resolve => setTimeout(resolve, intervalMs))

    if (signal?.aborted) return { status: 'aborted' }

    try {
      const templates = await fetchAIGenerateTemplates(api, entityType, entityId)
      consecutiveFailures = 0
      const entry = templates?.[aiTemplateKey(systemPromptTemplateId)]

      if (entry?.status === 'done' && entry.result) {
        return { status: 'done', result: entry.result }
      }
      if (entry?.status === 'failed') {
        return { status: 'failed', error: entry.error ?? 'Generation failed' }
      }
    } catch (err) {
      consecutiveFailures++
      logAIGenerationError('Poll request failed', err)
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        return { status: 'failed', error: 'Too many consecutive poll failures' }
      }
    }
  }

  return { status: 'timeout' }
}

async function fetchAIGenerateTemplates(
  api: Api,
  entityType: PollEntityType,
  entityId: string,
): Promise<Record<string, AIGenerateTemplateEntry> | null | undefined> {
  switch (entityType) {
    case 'cm': {
      const cm = await api.getContentModule({ task_id: entityId })
      return cm.aiGenerateTemplates
    }
    case 'step': {
      const step = await api.getStep({ step_id: entityId })
      return step.aiGenerateTemplates
    }
    case 'content': {
      const content = await api.getGeneralInfo(entityId)
      return content.aiGenerateTemplates
    }
  }
}
